import { useEffect, useState } from 'react'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'
import { Link, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import type { components } from '../../api/schema'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useToast } from '../../hooks/use-toast'
import { useCan } from '../../contexts/PermissionsContext'
import { useContractTypes } from '../../hooks/useQueries'
import { useLocale } from '../../contexts/LocaleContext'
import { useFormatters } from '../../lib/format'
import { getLocalizedName } from '../../lib/localization'
import { apiErrorDetail } from '../../lib/apiError'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Checkbox } from '../ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Skeleton } from '../ui/skeleton'
import { Textarea } from '../ui/textarea'
import { contractTypeLabel, CurrencySelect, fromMinor, ListingStatusBadge, toMinor, useRefreshListings, type Listing } from './listingShared'
import { ScreeningPolicyCard } from '../rentalApplications/ScreeningPolicyCard'

type Blocker = components['schemas']['ListingBlocker']
type Disclosure = components['schemas']['ListingDisclosure']
type Frequency = 'weekly' | 'four_weekly' | 'monthly' | 'quarterly' | 'annual'

const FREQUENCIES: Frequency[] = ['weekly', 'four_weekly', 'monthly', 'quarterly', 'annual']
const CHARGES_MODES = ['provision', 'flat_fee', 'included', 'none'] as const
const LANGS = ['en', 'fr'] as const
const EXCEPTION_NONE = '__none__'

type Form = {
    contractType: string
    rent: string
    currency: string
    frequency: string
    charges: string
    chargesMode: string
    availableFrom: string
    minTerm: string
    maxTerm: string
    maxOccupants: string
    title: Record<string, string>
    description: Record<string, string>
    disclosureValues: Record<string, string>
    exceptionCode: string
    exceptionJustification: string
}

const intOrNull = (s: string) => (s.trim() === '' ? null : Number(s))

function formFrom(l: Listing): Form {
    return {
        contractType: l.contract_type_code,
        rent: fromMinor(l.asking_rent_minor),
        currency: l.currency,
        frequency: l.rent_frequency ?? '',
        charges: fromMinor(l.charges_amount_minor),
        chargesMode: l.charges_mode ?? '',
        availableFrom: l.available_from_source === 'override' ? (l.available_from ?? '') : '',
        minTerm: l.min_term_months == null ? '' : String(l.min_term_months),
        maxTerm: l.max_term_months == null ? '' : String(l.max_term_months),
        maxOccupants: l.max_occupants == null ? '' : String(l.max_occupants),
        title: l.title ?? {},
        description: l.description ?? {},
        disclosureValues: l.disclosure_values ?? {},
        exceptionCode: l.rent_cap_exception?.code ?? '',
        exceptionJustification: l.rent_cap_exception?.justification ?? '',
    }
}

/** The editor of one listing: terms, text, disclosures, media, channels, timeline and the gated publication. */
export function ListingEditor() {
    const { org, listingUid } = useParams({ from: '/$org/listings/$listingUid' })
    const { t } = useTranslation('listings')
    const { toast } = useToast()
    const { locale } = useLocale()
    const { formatDate, formatMoney } = useFormatters()
    const canWrite = useCan('listings.write')
    const canReadContracts = useCan('contracts.read')
    const refresh = useRefreshListings()
    const path = { orgCode: org, listingUid }

    const { data, isLoading } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/listings/{listingUid}', { params: { path } })
    const listing = data?.listing
    const disclosures: Disclosure[] = data?.disclosures ?? []
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('title'), path: `/${org}/listings` },
        { title: listing?.asset_name ?? '…' },
    ])

    const [form, setForm] = useState<Form | null>(null)
    useEffect(() => {
        if (listing) setForm(formFrom(listing))
    }, [listing?.uid, listing?.updated_at]) // eslint-disable-line react-hooks/exhaustive-deps
    const patchForm = (p: Partial<Form>) => setForm((f) => (f ? { ...f, ...p } : f))

    const [checkOpen, setCheckOpen] = useState(false)
    const [publishOpen, setPublishOpen] = useState(false)
    const check = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/listings/{listingUid}/publication-check',
        { params: { path } },
        { enabled: false, gcTime: 0 },
    )
    const blockers: Blocker[] = check.data?.blockers ?? []

    const onError = (err: unknown) => toast({ title: t('error'), description: apiErrorDetail(err, t('error')), variant: 'destructive' })
    const done = () => { refresh(); setCheckOpen(false) }
    const save = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/listings/{listingUid}', { onSuccess: done, onError })
    const publish = $api.useMutation('post', '/api/v1/organizations/{orgCode}/listings/{listingUid}/publish', {
        onSuccess: () => { done(); setPublishOpen(false) },
        onError: (err) => { onError(err); check.refetch() },
    })
    const pause = $api.useMutation('post', '/api/v1/organizations/{orgCode}/listings/{listingUid}/pause', { onSuccess: done, onError })
    const withdraw = $api.useMutation('post', '/api/v1/organizations/{orgCode}/listings/{listingUid}/withdraw', { onSuccess: done, onError })

    const status = listing?.status ?? 'draft'
    const termsEditable = canWrite && (status === 'draft' || status === 'paused')
    const textEditable = canWrite && (status === 'draft' || status === 'paused' || status === 'published')

    // Rent cap: read off the asking-rent-cap endpoint for the saved rent.
    const capQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/asking-rent-cap',
        {
            params: {
                path: { orgCode: org, assetUid: listing?.asset_uid ?? '' },
                query: {
                    contract_type_code: listing?.contract_type_code ?? '',
                    rent_minor: listing?.asking_rent_minor ?? 0,
                    frequency: (listing?.rent_frequency ?? 'monthly') as Frequency,
                },
            },
        },
        { enabled: canReadContracts && !!listing && listing.asking_rent_minor != null && !!listing.rent_frequency },
    )
    const cap = canReadContracts ? capQuery.data : undefined
    const { data: contractTypes = [] } = useContractTypes(org, { enabled: !!listing })
    const leaseTypes = contractTypes.filter((ct) => ct.code?.startsWith('LEASE'))

    if (isLoading || !listing || !form) return <Skeleton className="h-64 w-full" data-testid="listing-loading" />

    const saveForm = () => {
        const text = { title: form.title, description: form.description, disclosure_values: form.disclosureValues }
        const body: Record<string, unknown> = termsEditable
            ? {
                ...text,
                contract_type_code: form.contractType,
                asking_rent_minor: toMinor(form.rent),
                currency: form.currency,
                rent_frequency: form.frequency || null,
                charges_amount_minor: toMinor(form.charges),
                charges_mode: form.chargesMode || null,
                available_from: form.availableFrom || null,
                min_term_months: intOrNull(form.minTerm),
                max_term_months: intOrNull(form.maxTerm),
                max_occupants: intOrNull(form.maxOccupants),
                rent_cap_exception: form.exceptionCode ? { code: form.exceptionCode, justification: form.exceptionJustification } : null,
            }
            : text
        save.mutate({ params: { path }, body })
    }

    const runCheck = async () => {
        setCheckOpen(true)
        await check.refetch()
    }
    const openPublish = async () => {
        setPublishOpen(true)
        await check.refetch()
    }

    const download = async (format: 'txt' | 'json' | 'zip') => {
        const params = { path, query: { lang: locale } }
        const { data: blob, error } = format === 'zip'
            ? await apiClient.GET('/api/v1/organizations/{orgCode}/listings/{listingUid}/export.zip', { params, parseAs: 'blob' })
            : await apiClient.GET('/api/v1/organizations/{orgCode}/listings/{listingUid}/export', { params: { path, query: { format, lang: locale } }, parseAs: 'blob' })
        if (error || !blob) return onError(error)
        const url = URL.createObjectURL(blob as Blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `listing-${listing.uid}.${format}`
        a.click()
        URL.revokeObjectURL(url)
    }

    const blockerLabel = (b: Blocker): string => {
        const label = b.label
        if (typeof label === 'string') return label
        if (label && typeof label === 'object') return getLocalizedName(label as Record<string, string>, locale)
        return t(`blocker.kind.${b.kind}`, { defaultValue: b.code })
    }
    const renderBlockers = () => (
        <ul className="space-y-2" data-testid="listing-blockers">
            {blockers.map((b, i) => (
                <li key={`${b.kind}-${b.code}-${i}`} className="rounded border p-2 text-sm" data-testid="listing-blocker-row" data-kind={b.kind} data-code={b.code}>
                    <span className="font-medium">{blockerLabel(b)}</span>
                    <span className="block text-muted-foreground">{t(`action.${b.action}`, { defaultValue: b.action })}</span>
                </li>
            ))}
        </ul>
    )

    return (
        <div className="space-y-6" data-testid="listing-page" data-status={status}>
            <PageHeader
                icon={SECTION_ICONS.listings}
                title={listing.asset_name}
                description={
                    <Link to="/$org/assets/$assetUid" params={{ org, assetUid: listing.asset_uid }} className="hover:underline" data-testid="listing-asset-link">
                        {t('editor.open_asset')}
                    </Link>
                }
                titleAside={<ListingStatusBadge status={status} testId="listing-status" />}
                actions={
                    <div className="flex flex-wrap gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" data-testid="listing-export-menu">{t('export.button')}</Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                                {(['txt', 'json', 'zip'] as const).map((f) => (
                                    <DropdownMenuItem key={f} onSelect={() => download(f)} data-testid={`listing-export-${f}`}>
                                        {t(`export.${f}`)}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {canWrite && (status === 'draft' || status === 'paused') && (
                            <>
                                <Button variant="outline" onClick={runCheck} data-testid="listing-check-button">{t('check.button')}</Button>
                                <Button onClick={openPublish} data-testid="listing-publish-button">{t('publish.button')}</Button>
                            </>
                        )}
                        {canWrite && status === 'published' && (
                            <Button variant="outline" onClick={() => pause.mutate({ params: { path } })} disabled={pause.isPending} data-testid="listing-pause-button">
                                {t('pause.button')}
                            </Button>
                        )}
                        {canWrite && status !== 'withdrawn' && status !== 'let' && (
                            <Button variant="outline" onClick={() => withdraw.mutate({ params: { path } })} disabled={withdraw.isPending} data-testid="listing-withdraw-button">
                                {t('withdraw.button')}
                            </Button>
                        )}
                    </div>
                }
            />

            {status === 'published' && <p className="text-sm text-muted-foreground" data-testid="listing-published-note">{t('editor.published_note')}</p>}

            {checkOpen && !check.isFetching && (
                <Card data-testid="listing-check-result" data-publishable={String(!!check.data?.publishable)}>
                    <CardHeader className="pb-2"><CardTitle className="text-base">{check.data?.publishable ? t('check.ok') : t('check.blocked')}</CardTitle></CardHeader>
                    {!check.data?.publishable && <CardContent>{renderBlockers()}</CardContent>}
                </Card>
            )}

            <Card data-testid="listing-object-card">
                <CardHeader className="pb-2"><CardTitle className="text-base">{t('editor.object')}</CardTitle></CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                    <div>
                        <Label className="mb-1 block text-sm">{t('field.object')}</Label>
                        <Link to="/$org/assets/$assetUid" params={{ org, assetUid: listing.asset_uid }} className="text-sm hover:underline" data-testid="listing-object-name">
                            {listing.asset_name}
                        </Link>
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('field.contract_type')}</Label>
                        <Select value={form.contractType} onValueChange={(v) => patchForm({ contractType: v })} disabled={!termsEditable}>
                            <SelectTrigger data-testid="listing-contract-type-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {(leaseTypes.some((ct) => ct.code === form.contractType) ? leaseTypes : [...leaseTypes, { code: form.contractType, name: form.contractType }]).map((ct) => (
                                    <SelectItem key={ct.code} value={ct.code} data-testid={`listing-contract-type-${ct.code}`}>
                                        {contractTypeLabel(ct, locale)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">{t('editor.rent')}</CardTitle></CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-3">
                    <div>
                        <Label className="mb-1 block text-sm" htmlFor="listing-rent-input">{t('field.rent')}</Label>
                        <Input id="listing-rent-input" type="number" step="0.01" value={form.rent} disabled={!termsEditable} onChange={(e) => patchForm({ rent: e.target.value })} data-testid="listing-rent-input" />
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('field.currency')}</Label>
                        <CurrencySelect value={form.currency} onChange={(v) => patchForm({ currency: v })} disabled={!termsEditable} testId="listing-currency-select" />
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('field.frequency')}</Label>
                        <Select value={form.frequency || undefined} onValueChange={(v) => patchForm({ frequency: v })} disabled={!termsEditable}>
                            <SelectTrigger data-testid="listing-frequency-select"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                                {FREQUENCIES.map((f) => <SelectItem key={f} value={f} data-testid={`listing-frequency-${f}`}>{t(`frequency.${f}`)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm" htmlFor="listing-charges-input">{t('field.charges')}</Label>
                        <Input id="listing-charges-input" type="number" step="0.01" value={form.charges} disabled={!termsEditable} onChange={(e) => patchForm({ charges: e.target.value })} data-testid="listing-charges-input" />
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('field.charges_mode')}</Label>
                        <Select value={form.chargesMode || undefined} onValueChange={(v) => patchForm({ chargesMode: v })} disabled={!termsEditable}>
                            <SelectTrigger data-testid="listing-charges-mode-select"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                                {CHARGES_MODES.map((m) => <SelectItem key={m} value={m} data-testid={`listing-charges-mode-${m}`}>{t(`charges_mode.${m}`)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {cap && cap.status !== 'not_configured' && (
                        <div className="md:col-span-3 space-y-2 rounded border p-3 text-sm" data-testid="listing-rent-cap" data-status={cap.status}>
                            <p className="font-medium">
                                {t(`cap.${cap.status}`, {
                                    defaultValue: cap.status,
                                    cap: cap.cap_minor == null ? '' : formatMoney(cap.cap_minor, cap.currency ?? listing.currency),
                                })}
                            </p>
                            {cap.status === 'capped' && cap.exceptions.length > 0 && (
                                <div className="grid gap-2 md:grid-cols-2">
                                    <Select value={form.exceptionCode || EXCEPTION_NONE} onValueChange={(v) => patchForm({ exceptionCode: v === EXCEPTION_NONE ? '' : v })} disabled={!termsEditable}>
                                        <SelectTrigger data-testid="listing-rent-cap-exception-select"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={EXCEPTION_NONE}>{t('cap.no_exception')}</SelectItem>
                                            {cap.exceptions.map((c) => <SelectItem key={c} value={c} data-testid={`listing-rent-cap-exception-${c}`}>{c}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    {form.exceptionCode && (
                                        <Input
                                            value={form.exceptionJustification}
                                            disabled={!termsEditable}
                                            placeholder={t('cap.justification')}
                                            onChange={(e) => patchForm({ exceptionJustification: e.target.value })}
                                            data-testid="listing-rent-cap-exception-justification"
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">{t('editor.terms')}</CardTitle></CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-4">
                    <div className="md:col-span-2">
                        <Label className="mb-1 block text-sm" htmlFor="listing-available-from-input">{t('field.available_from')}</Label>
                        <div className="flex gap-2">
                            <Input id="listing-available-from-input" type="date" value={form.availableFrom || listing.available_from || ''} disabled={!termsEditable} onChange={(e) => patchForm({ availableFrom: e.target.value })} data-testid="listing-available-from-input" />
                            {termsEditable && form.availableFrom && (
                                <Button type="button" variant="ghost" onClick={() => patchForm({ availableFrom: '' })} data-testid="listing-available-from-reset">{t('field.available_from_reset')}</Button>
                            )}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground" data-testid="listing-available-from-source">
                            {t(`available_source.${form.availableFrom ? 'override' : (listing.available_from_source || 'none')}`, { defaultValue: listing.available_from_source })}
                            {listing.derived_available_from ? ` (${formatDate(listing.derived_available_from)})` : ''}
                        </p>
                    </div>
                    {([['minTerm', 'min_term'], ['maxTerm', 'max_term'], ['maxOccupants', 'max_occupants']] as const).map(([key, id]) => (
                        <div key={key}>
                            <Label className="mb-1 block text-sm" htmlFor={`listing-${id.replace('_', '-')}-input`}>{t(`field.${id}`)}</Label>
                            <Input id={`listing-${id.replace('_', '-')}-input`} type="number" min={0} value={form[key]} disabled={!termsEditable} onChange={(e) => patchForm({ [key]: e.target.value })} data-testid={`listing-${id.replace('_', '-')}-input`} />
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">{t('editor.text')}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                    {LANGS.map((lang) => (
                        <div key={lang} className="space-y-2">
                            <Label className="block text-sm" htmlFor={`listing-title-input-${lang}`}>{t('field.title')} ({lang.toUpperCase()})</Label>
                            <Input id={`listing-title-input-${lang}`} value={form.title[lang] ?? ''} disabled={!textEditable} onChange={(e) => patchForm({ title: { ...form.title, [lang]: e.target.value } })} data-testid={`listing-title-input-${lang}`} />
                            <Textarea value={form.description[lang] ?? ''} disabled={!textEditable} rows={4} aria-label={`${t('field.description')} (${lang.toUpperCase()})`} onChange={(e) => patchForm({ description: { ...form.description, [lang]: e.target.value } })} data-testid={`listing-description-input-${lang}`} />
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Card data-testid="listing-disclosures">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">{t('editor.disclosures')}</CardTitle>
                    <p className="text-sm text-muted-foreground">{t('editor.disclosures_hint')}</p>
                </CardHeader>
                <CardContent className="space-y-3">
                    {disclosures.length === 0 && <p className="text-sm text-muted-foreground" data-testid="listing-disclosures-empty">{t('editor.no_disclosures')}</p>}
                    {disclosures.map((d) => (
                        <div key={d.code} className="rounded border p-3 text-sm" data-testid="listing-disclosure-row" data-code={d.code} data-status={d.status}>
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium">{getLocalizedName(d.label, locale)}</span>
                                <Badge variant={d.status === 'found' ? 'success' : 'warning'} data-testid="listing-disclosure-status">{t(`disclosure.${d.status}`)}</Badge>
                                {d.indicative && <Badge variant="outline" data-testid="listing-disclosure-indicative">{t('disclosure.indicative')}</Badge>}
                            </div>
                            {d.description && <p className="mt-1 text-muted-foreground">{getLocalizedName(d.description, locale)}</p>}
                            {d.value_source === 'manual' ? (
                                <Input
                                    className="mt-2"
                                    value={form.disclosureValues[d.code] ?? ''}
                                    disabled={!textEditable}
                                    aria-label={getLocalizedName(d.label, locale)}
                                    onChange={(e) => patchForm({ disclosureValues: { ...form.disclosureValues, [d.code]: e.target.value } })}
                                    data-testid="listing-disclosure-value-input"
                                />
                            ) : d.status === 'found' ? (
                                <p className="mt-1" data-testid="listing-disclosure-value">{typeof d.value === 'object' ? JSON.stringify(d.value) : String(d.value ?? '')}</p>
                            ) : (
                                <p className="mt-1 text-muted-foreground" data-testid="listing-disclosure-action">{t(`action.${d.action ?? 'open_property'}`, { defaultValue: d.action ?? '' })}</p>
                            )}
                        </div>
                    ))}
                </CardContent>
            </Card>

            {canWrite && (
                <div className="flex justify-end">
                    <Button onClick={saveForm} disabled={save.isPending} data-testid="listing-save-button">{t('save')}</Button>
                </div>
            )}

            <MediaCard org={org} listing={listing} canEdit={termsEditable} onError={onError} />
            <ChannelsCard org={org} listing={listing} canEdit={canWrite && status !== 'withdrawn'} onError={onError} />
            <ScreeningPolicyCard org={org} assetUid={listing.asset_uid} listingUid={listing.uid} />

            <Card data-testid="listing-events">
                <CardHeader className="pb-2"><CardTitle className="text-base">{t('editor.events')}</CardTitle></CardHeader>
                <CardContent><EventsList org={org} listingUid={listingUid} /></CardContent>
            </Card>

            <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
                <DialogContent data-testid="listing-publish-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('publish.title')}</DialogTitle>
                        <DialogDescription>{t('publish.description')}</DialogDescription>
                    </DialogHeader>
                    {check.isFetching ? <Skeleton className="h-20 w-full" /> : check.data?.publishable ? (
                        <p className="text-sm" data-testid="listing-publish-ready">{t('check.ok')}</p>
                    ) : <div className="max-h-[50vh] overflow-y-auto">{renderBlockers()}</div>}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPublishOpen(false)} data-testid="listing-publish-cancel">{t('cancel')}</Button>
                        <Button
                            onClick={() => publish.mutate({ params: { path } })}
                            disabled={check.isFetching || !check.data?.publishable || publish.isPending}
                            data-testid="listing-publish-confirm"
                        >
                            {t('publish.confirm')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}

const mediaUids = (l: Listing): string[] => (l.documents ?? []).flatMap((d) => (d.document_uid ? [d.document_uid] : []))

function MediaCard({ org, listing, canEdit, onError }: { org: string; listing: Listing; canEdit: boolean; onError: (e: unknown) => void }) {
    const { t } = useTranslation('listings')
    const refresh = useRefreshListings()
    const [selected, setSelected] = useState<string[]>(mediaUids(listing))
    useEffect(() => setSelected(mediaUids(listing)), [listing.updated_at, listing.documents]) // eslint-disable-line react-hooks/exhaustive-deps
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/documents', {
        params: { path: { orgCode: org }, query: { asset_uid: listing.asset_uid, limit: 100 } },
    })
    const docs = data?.data ?? []
    const save = $api.useMutation('put', '/api/v1/organizations/{orgCode}/listings/{listingUid}/documents', { onSuccess: refresh, onError })
    const toggle = (uid: string, on: boolean) => setSelected((s) => (on ? [...s, uid] : s.filter((x) => x !== uid)))
    const move = (uid: string, delta: number) => setSelected((s) => {
        const i = s.indexOf(uid)
        const j = i + delta
        if (i < 0 || j < 0 || j >= s.length) return s
        const next = [...s]
        ;[next[i], next[j]] = [next[j], next[i]]
        return next
    })
    // Chosen documents first, in the order sent; the others after.
    const ordered = [
        ...selected.flatMap((uid) => docs.filter((d) => d.uid === uid)),
        ...docs.filter((d) => !selected.includes(d.uid)),
    ]
    return (
        <Card data-testid="listing-media-picker">
            <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('editor.media')}</CardTitle>
                <p className="text-sm text-muted-foreground">{t('editor.media_hint')}</p>
            </CardHeader>
            <CardContent className="space-y-2">
                {docs.length === 0 && <p className="text-sm text-muted-foreground" data-testid="listing-media-empty">{t('editor.no_media')}</p>}
                {ordered.map((d) => {
                    const pos = selected.indexOf(d.uid)
                    return (
                        <div key={d.uid} className="flex items-center gap-2 text-sm" data-testid="listing-media-item" data-document-uid={d.uid} data-position={pos >= 0 ? pos + 1 : undefined}>
                            <Checkbox id={`media-${d.uid}`} checked={pos >= 0} disabled={!canEdit} onCheckedChange={(v) => toggle(d.uid, v === true)} data-testid="listing-media-checkbox" />
                            <label htmlFor={`media-${d.uid}`} className="flex-1">{d.name}</label>
                            {canEdit && pos >= 0 && (
                                <>
                                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={pos === 0} onClick={() => move(d.uid, -1)} aria-label={t('editor.media_up')} data-testid="listing-media-up"><AppIcon path={mdiChevronUp} size="sm" /></Button>
                                    <Button size="icon" variant="ghost" className="h-7 w-7" disabled={pos === selected.length - 1} onClick={() => move(d.uid, 1)} aria-label={t('editor.media_down')} data-testid="listing-media-down"><AppIcon path={mdiChevronDown} size="sm" /></Button>
                                </>
                            )}
                        </div>
                    )
                })}
                {canEdit && docs.length > 0 && (
                    <Button size="sm" onClick={() => save.mutate({ params: { path: { orgCode: org, listingUid: listing.uid } }, body: { document_uids: selected } })} disabled={save.isPending} data-testid="listing-media-save">
                        {t('editor.media_save')}
                    </Button>
                )}
            </CardContent>
        </Card>
    )
}

function ChannelsCard({ org, listing, canEdit, onError }: { org: string; listing: Listing; canEdit: boolean; onError: (e: unknown) => void }) {
    const { t } = useTranslation('listings')
    const refresh = useRefreshListings()
    const [code, setCode] = useState('')
    const [reference, setReference] = useState('')
    const path = { orgCode: org, listingUid: listing.uid }
    const add = $api.useMutation('post', '/api/v1/organizations/{orgCode}/listings/{listingUid}/channels', {
        onSuccess: () => { setCode(''); setReference(''); refresh() },
        onError,
    })
    const remove = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/listings/{listingUid}/channels/{channelUid}', { onSuccess: refresh, onError })
    const channels = (listing.channels ?? []).filter((c) => c.status !== 'removed')
    return (
        <Card data-testid="listing-channels">
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('editor.channels')}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
                {channels.map((c) => (
                    <div key={c.uid} className="flex items-center justify-between rounded border p-2 text-sm" data-testid="listing-channel-row">
                        <span><span className="font-medium">{c.channel_code}</span> <span className="text-muted-foreground">{c.external_reference}</span></span>
                        {canEdit && (
                            <Button variant="ghost" size="sm" onClick={() => remove.mutate({ params: { path: { ...path, channelUid: c.uid ?? '' } } })} data-testid="listing-channel-remove">
                                {t('editor.channel_remove')}
                            </Button>
                        )}
                    </div>
                ))}
                {canEdit && (
                    <div className="flex flex-wrap items-end gap-2">
                        <Input className="w-40" placeholder={t('editor.channel_code')} value={code} onChange={(e) => setCode(e.target.value)} data-testid="listing-channel-code-input" />
                        <Input className="w-56" placeholder={t('editor.channel_reference')} value={reference} onChange={(e) => setReference(e.target.value)} data-testid="listing-channel-reference-input" />
                        <Button size="sm" disabled={!code.trim() || !reference.trim() || add.isPending} onClick={() => add.mutate({ params: { path }, body: { channel_code: code.trim(), reference: reference.trim() } })} data-testid="listing-channel-add">
                            {t('editor.channel_add')}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

function EventsList({ org, listingUid }: { org: string; listingUid: string }) {
    const { t } = useTranslation('listings')
    const { formatDate } = useFormatters()
    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/listings/{listingUid}/events', { params: { path: { orgCode: org, listingUid } } })
    const events = data?.events ?? []
    if (events.length === 0) return <p className="text-sm text-muted-foreground" data-testid="listing-events-empty">{t('editor.no_events')}</p>
    return (
        <ul className="space-y-1 text-sm">
            {events.map((e) => (
                <li key={e.uid} data-testid="listing-event" data-kind={e.kind}>
                    {formatDate(e.occurred_at ?? '')} · {t(`event.${e.kind}`, { defaultValue: e.kind })}
                </li>
            ))}
        </ul>
    )
}
